/**
 * Human copy for the send flow's two machine vocabularies: the batch skip
 * reasons (BE sending.SkipReason) and the send route's rejection codes.
 *
 * Both maps are the only place a raw enum is turned into words, so the dialog,
 * the progress panel and the summary toast can never drift apart.
 */
import type { OutreachSkipReason } from '@discovr/types';

export const SKIP_LABELS: Record<OutreachSkipReason, string> = {
  suppressed: 'Do not contact',
  removed: 'Removed',
  replied: 'Already replied',
  closed: 'Closed',
  campaign_paused: 'Outreach paused',
  mailbox_unavailable: 'Mailbox unavailable',
  no_contact: 'No email on file',
  already_contacted: 'Already contacted',
  not_in_outreach: 'Not in outreach',
  with_brand: 'With the brand',
  already_removed: 'Already removed',
  has_placeholder: 'Has a blank to fill in',
  on_hold: 'On hold',
};

/** An unknown reason reads as itself rather than disappearing. */
export function skipLabel(reason: string): string {
  return SKIP_LABELS[reason as OutreachSkipReason] ?? reason.replace(/_/g, ' ');
}

export const SEND_ERROR_LABELS: Record<string, string> = {
  CAMPAIGN_PAUSED: 'Outreach is paused for this campaign. Resume it in Settings, then try again.',
  NO_CONTACT: 'This creator has no email on file. Add one in their contact details, then try again.',
  ADDRESS_SUPPRESSED: 'This address is on the do-not-contact list. Remove it under Settings → Outreach → Suppressions to email it.',
  ENTRY_NOT_SENDABLE: "A first email only goes to a creator who hasn't been contacted. This creator is past that stage.",
  MESSAGE_ALREADY_LIVE: 'A first email to this creator is already queued or sent.',
  NO_SEQUENCE_TEMPLATE: 'Pick a sequence template in Settings, or write the message here.',
  TEMPLATE_ARCHIVED: 'The sequence template was archived. Pick another in Settings.',
  BODY_REQUIRED: 'Add a message before sending.',
  DRAFT_SUBJECT_REQUIRED: 'Add a subject before sending. The first email needs one.',
  DRAFT_BODY_REQUIRED: 'Add a message before sending.',
  SCHEDULE_TOO_FAR: 'Pick a time within the next 30 days.',
  SCHEDULE_IN_PAST: 'That time has passed. Pick a later time, or choose Send now.',
  DRAFT_BODY_TOO_LONG: 'This email is over the 2,000-character limit. Shorten it and try again.',
  DRAFT_SUBJECT_TOO_LONG: 'This subject is over the 60-character limit. Shorten it and try again.',
  INVALID_SEND_TZ: "That time zone isn't recognised. Pick another one and try again.",
  MAILBOX_UNAVAILABLE: 'That mailbox is paused or disabled. Pick another, or choose Auto.',
  DRAFT_HAS_PLACEHOLDER: 'This message still has a blank to fill in. Edit it before sending.',
  FIRST_TOUCH_LINK: "The first email can't contain a link. Remove it, or add it to a follow-up.",
  FORMATTING_WITHOUT_TEXT: 'This message has formatting but no words. Add a message before sending.',
  KIND_NOT_SUPPORTED_YET: 'Only first touches can be sent in this release.',
};
